import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, LogOut, User, Trash2, AlertTriangle, Download, Smartphone, Fingerprint, CheckCircle, XCircle, Bell, BellOff, Settings as SettingsIcon, Users, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { isLoggedIn, logout } from "../utils/auth";
import { APP_VERSION } from "../version";
import { usePWAInstall } from "@/components/PWAInstallBanner";
import { 
  isBiometricSupported, 
  isPlatformAuthenticatorAvailable, 
  getBiometricStatus, 
  registerBiometric, 
  disableBiometric,
  clearBiometricUsername 
} from "@/utils/biometric";
import {
  getNotificationStatus,
  enableNotifications,
  disableNotifications,
  sendTestNotification,
  loadNotificationSettings,
  saveNotificationSettings
} from "@/lib/notificationService";

import { userStorage } from '../utils/userStorage';
export default function Settings() {
  const [formData, setFormData] = useState({
    company_name: "",
    street: "",
    postal_code: "",
    city: "",
    ust_id: "",
    iban: "",
    bic: "",
    bank_name: ""
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricDebug, setBiometricDebug] = useState({ webauthn: false, platform: false });
  const [biometricStatus, setBiometricStatus] = useState({ biometric_enabled: false, devices: [] });
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [musicDismissed, setMusicDismissed] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem('bt_music_dismissed') === '1'
  );
  const [notificationStatus, setNotificationStatus] = useState({ supported: false, permission: 'default', enabled: false });
  const [notificationSettings, setNotificationSettings] = useState({ reminder_minutes: 15 });

  const CONTACT_THRESHOLDS_KEY = 'burningteam_contact_thresholds';
  const [contactThresholds, setContactThresholds] = useState(() => {
    try {
      const stored = JSON.parse(userStorage.getItem('burningteam_contact_thresholds') || '{}');
      return {
        yellowEnabled: stored.yellowEnabled !== false,
        yellowDays: stored.yellowDays ?? 30,
        redEnabled: stored.redEnabled !== false,
        redDays: stored.redDays ?? 60,
      };
    } catch {
      return { yellowEnabled: true, yellowDays: 30, redEnabled: true, redDays: 60 };
    }
  });
  const [showContactSettings, setShowContactSettings] = useState(false);
  const navigate = useNavigate();
  const { isInstalled, isInstallable, promptInstall } = usePWAInstall();

  useEffect(() => {
    loadCurrentUser();
    loadSettings();
    checkBiometricSupport();
  }, []);

  const checkBiometricSupport = async () => {
    const supported = isBiometricSupported();
    let platformAvailable = false;
    
    try {
      platformAvailable = await isPlatformAuthenticatorAvailable();
    } catch (e) {
      console.error("Platform authenticator check failed:", e);
    }
    
    setBiometricDebug({ webauthn: supported, platform: platformAvailable });
    setBiometricSupported(supported && platformAvailable);
    
    if (supported && platformAvailable) {
      const token = localStorage.getItem('token');
      if (token) {
        const status = await getBiometricStatus(token);
        setBiometricStatus(status);
      }
    }
  };

  // Check notification status on mount
  useEffect(() => {
    loadNotificationSettingsFromBackend();
  }, []);

  const loadNotificationSettingsFromBackend = async () => {
    const token = localStorage.getItem('token');
    
    // Get browser notification status
    const browserStatus = getNotificationStatus();
    
    if (!token) {
      // Fallback to local settings if not logged in
      const settings = loadNotificationSettings();
      setNotificationSettings(settings);
      setNotificationStatus({
        ...browserStatus,
        enabled: settings.enabled && browserStatus.permission === 'granted'
      });
      return;
    }
    
    try {
      const response = await axios.get(`${API}/notifications/settings`);
      const backendSettings = response.data;
      setNotificationSettings(backendSettings);
      
      // Also save to local storage for consistency
      saveNotificationSettings(backendSettings);
      
      // Set status based on backend settings AND browser permission
      setNotificationStatus({
        ...browserStatus,
        enabled: backendSettings.enabled && browserStatus.permission === 'granted'
      });
    } catch (error) {
      console.log("Loading local notification settings");
      const settings = loadNotificationSettings();
      setNotificationSettings(settings);
      setNotificationStatus({
        ...browserStatus,
        enabled: settings.enabled && browserStatus.permission === 'granted'
      });
    }
  };

  const handleEnableNotifications = async () => {
    try {
      const result = await enableNotifications();
      
      if (result) {
        // Get the FCM token (either returned from enableNotifications or from localStorage)
        let fcmToken = typeof result === 'string' && result.length > 50 ? result : localStorage.getItem('fcmToken');
        
        if (fcmToken && fcmToken.length > 50) {
          // Register real FCM token with backend and reset to default settings
          try {
            const subscribeResponse = await axios.post(`${API}/notifications/subscribe`, { fcm_token: fcmToken });
            
            // Reset to default times when enabling
            const defaultSettings = {
              enabled: true,
              appointment_reminder: true,
              reminder_minutes: 15,
              daily_overview: true,
              daily_overview_time: "07:00",
              focus_reminder: true,
              focus_reminder_time: "07:00",
              weekly_planning: true,
              weekly_planning_time: "19:00",
              monthly_planning: true,
              monthly_planning_time: "10:00",
              motivational_quote: true,
              motivational_quote_time: "08:00",
              followup_reminder: true,
              followup_reminder_time: "09:00",
              followup_7_enabled: true,
              followup_14_enabled: true,
              followup_21_enabled: true,
              followup_28_enabled: true
            };
            
            await axios.put(`${API}/notifications/settings`, defaultSettings);
            
            await loadNotificationSettingsFromBackend();
            toast.success("Benachrichtigungen aktiviert!");
          } catch (error) {
            console.error("Error registering FCM token:", error);
            toast.error("Fehler beim Registrieren der Benachrichtigungen");
          }
        } else {
          toast.warning("Benachrichtigungen aktiviert (ohne Push-Token)");
        }
        setNotificationStatus(getNotificationStatus());
      } else {
        toast.error("Benachrichtigungen konnten nicht aktiviert werden. Bitte erlaube sie in den Browser-Einstellungen.");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  const handleDisableNotifications = async () => {
    disableNotifications();
    try {
      await axios.post(`${API}/notifications/unsubscribe`);
    } catch (error) {
      console.error("Error unsubscribing:", error);
    }
    toast.success("Benachrichtigungen deaktiviert");
    setNotificationStatus(getNotificationStatus());
  };

  const handleTestNotification = async () => {
    // Try backend first (real Firebase push)
    try {
      const response = await axios.post(`${API}/notifications/test`);
      const data = response.data;
      
      if (data.success) {
        toast.success("Push über Firebase gesendet!");
      } else {
        toast.error(data.message);
        // Fallback to local notification
        sendTestNotification();
      }
    } catch (error) {
      toast.error("Backend nicht erreichbar - lokale Benachrichtigung");
      sendTestNotification();
    }
  };

  const handleNotificationSettingChange = async (key, value) => {
    // Update UI immediately
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    
    // Save to backend first (source of truth)
    try {
      const response = await axios.put(`${API}/notifications/settings`, { [key]: value });
      // Use backend response as the source of truth
      const savedSettings = response.data;
      setNotificationSettings(savedSettings);
      saveNotificationSettings(savedSettings);
      
      // Show success for time changes
      if (key.includes('_time')) {
        toast.success("Zeit gespeichert");
      }
    } catch (error) {
      console.error("Error saving notification setting:", error);
      toast.error("Einstellung konnte nicht gespeichert werden");
      // Reload settings from backend on error
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API}/notifications/settings`);
          setNotificationSettings(response.data);
          saveNotificationSettings(response.data);
        } catch (e) {
          console.error("Error reloading settings:", e);
        }
      }
    }
  };

  const handleReminderMinutesChange = (minutes) => {
    handleNotificationSettingChange('reminder_minutes', minutes);
    toast.success(`Erinnerung auf ${minutes} Minuten vorher gesetzt`);
  };

  const handleContactThresholdChange = (key, value) => {
    const updated = { ...contactThresholds, [key]: value };
    setContactThresholds(updated);
    userStorage.setItem('burningteam_contact_thresholds', JSON.stringify(updated));
  };

  // Open system app notification settings
  const openAppNotificationSettings = () => {
    // Detect platform and try to open app settings
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = userAgent.includes('android');
    const isIOS = userAgent.includes('iphone') || userAgent.includes('ipad');
    
    if (isAndroid) {
      // Try to open Android app settings via intent
      // This works for PWAs installed on Android
      try {
        // Try the notification settings intent
        window.location.href = 'intent://settings/notifications#Intent;scheme=android-app;end';
      } catch (e) {
        // Fallback: Show instructions
        toast.info(
          "Öffne die Android-Einstellungen → Apps → BurningTeam → Benachrichtigungen",
          { duration: 8000 }
        );
      }
    } else if (isIOS) {
      // iOS doesn't allow direct links to settings, show instructions
      toast.info(
        "Öffne die iOS-Einstellungen → BurningTeam → Mitteilungen",
        { duration: 8000 }
      );
    } else {
      // Desktop browser - show instructions
      toast.info(
        "Klicke auf das Schloss-Symbol in der Adressleiste → Website-Einstellungen → Benachrichtigungen",
        { duration: 8000 }
      );
    }
  };

  const handleEnableBiometric = async () => {
    setBiometricLoading(true);
    try {
      const token = localStorage.getItem('token');
      const deviceName = navigator.userAgent.includes('Android') 
        ? 'Android Gerät' 
        : navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')
          ? 'Apple Gerät'
          : 'Mein Gerät';
          
      await registerBiometric(token, deviceName);
      toast.success("Biometrische Anmeldung aktiviert!");
      await checkBiometricSupport();
    } catch (error) {
      if (!error.message?.includes("cancelled") && !error.message?.includes("aborted")) {
        toast.error(error.message || "Aktivierung fehlgeschlagen");
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleDisableBiometric = async () => {
    setBiometricLoading(true);
    try {
      const token = localStorage.getItem('token');
      await disableBiometric(token);
      clearBiometricUsername();
      toast.success("Biometrische Anmeldung deaktiviert");
      setBiometricStatus({ biometric_enabled: false, devices: [] });
    } catch (error) {
      toast.error(error.message || "Deaktivierung fehlgeschlagen");
    } finally {
      setBiometricLoading(false);
    }
  };

  const loadCurrentUser = async () => {
    if (!isLoggedIn()) return;
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der User-Daten:", error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setFormData(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error("Fehler beim Laden der Einstellungen");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/settings`, formData);
      toast.success("Einstellungen erfolgreich gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern der Einstellungen");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Erfolgreich abgemeldet");
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/delete-account`);
      logout();
      toast.success("Account erfolgreich gelöscht");
      navigate('/register');
    } catch (error) {
      toast.error("Fehler beim Löschen des Accounts");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Link to="/main-hub">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu Mein Business
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <div className="flex items-start justify-between mb-2 gap-3">
            <h1 className="text-3xl font-bold text-slate-800">Profil</h1>
            <span
              data-testid="settings-app-version"
              className="text-[11px] text-slate-400 font-mono tracking-wide mt-2 select-text"
              title="App-Version"
            >
              v{APP_VERSION}
            </span>
          </div>
          <p className="text-slate-500 mb-8">Benutzerkonto & Firmendaten verwalten</p>
          
          {/* User Account Section */}
          <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">Benutzerkonto</h2>
            
            {isLoggedIn() && currentUser ? (
              <div>
                <div className="flex items-center gap-3 mb-4 p-3 bg-white rounded-lg border border-blue-200">
                  <User className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="font-semibold text-slate-800">{currentUser.username}</p>
                    <p className="text-sm text-slate-600">{currentUser.email}</p>
                    {currentUser.full_name && (
                      <p className="text-sm text-slate-500">{currentUser.full_name}</p>
                    )}
                  </div>
                </div>
                <Button 
                  onClick={handleLogout}
                  variant="outline"
                  className="border-red-300 hover:bg-red-50 text-red-600"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Ausloggen
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-slate-600 mb-4">
                  Melden Sie sich an oder erstellen Sie ein Konto, um Ihre persönlichen Daten zu schützen.
                </p>
                <div className="flex gap-3 flex-wrap">
                  <Link to="/login">
                    <Button 
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013 3v1" />
                      </svg>
                      Anmelden
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button 
                      variant="outline"
                      className="border-blue-300 hover:bg-blue-50 text-blue-600"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                      Registrieren
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Company Settings Section */}
          <h2 className="text-xl font-semibold text-slate-800 mb-4">Firmendaten</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label htmlFor="company_name">Firmenname *</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  placeholder="Meine Firma GmbH"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="street">Straße & Hausnummer *</Label>
                <Input
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  required
                  placeholder="Musterstraße 123"
                />
              </div>

              <div>
                <Label htmlFor="postal_code">PLZ *</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  required
                  placeholder="12345"
                />
              </div>

              <div>
                <Label htmlFor="city">Ort *</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  placeholder="Musterstadt"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="ust_id">Steuernummer oder USt-IdNr.</Label>
                <Input
                  id="ust_id"
                  name="ust_id"
                  value={formData.ust_id}
                  onChange={handleChange}
                  placeholder="z.B. 12/345/67890 oder DE123456789"
                />
                <p className="text-sm text-slate-500 mt-1">Pflicht für Rechnungen über 250 € — eines von beiden reicht.</p>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  name="iban"
                  value={formData.iban}
                  onChange={handleChange}
                  placeholder="DE00 0000 0000 0000 0000 00"
                />
              </div>

              <div>
                <Label htmlFor="bic">BIC</Label>
                <Input
                  id="bic"
                  name="bic"
                  value={formData.bic}
                  onChange={handleChange}
                  placeholder="BANKDEFFXXX"
                />
              </div>

              <div>
                <Label htmlFor="bank_name">Bankname</Label>
                <Input
                  id="bank_name"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  placeholder="Musterbank"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full md:w-auto bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? "Speichern..." : "Einstellungen speichern"}
            </Button>
          </form>

          {/* PWA Install Section - Only show if NOT installed */}
          {!isInstalled && (
            <div className="mt-12 pt-8 border-t border-slate-200">
              <button 
                onClick={async () => {
                  if (isInstallable) {
                    const accepted = await promptInstall();
                    if (accepted) {
                      toast.success("App wird installiert!");
                    }
                  } else {
                    toast.info(
                      <div>
                        <p className="font-semibold mb-2">So installierst du die App:</p>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Chrome (PC):</strong> Klicke auf ⊕ in der Adressleiste</li>
                          <li>• <strong>Chrome (Android):</strong> Menü (⋮) → "Zum Startbildschirm hinzufügen"</li>
                          <li>• <strong>Safari (iOS):</strong> Teilen (↑) → "Zum Home-Bildschirm"</li>
                        </ul>
                        <p className="text-xs mt-2 text-slate-500">Tipp: Seite neu laden falls der Button nicht funktioniert</p>
                      </div>,
                      { duration: 10000 }
                    );
                  }
                }}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100 border border-emerald-200 rounded-lg text-left transition-colors"
              >
                <div className="w-10 h-10 min-w-[40px] bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800">BurningTeam App</p>
                  <p className="text-sm text-slate-600">Installiere die App für schnellen Zugriff</p>
                </div>
                <Download className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              </button>
            </div>
          )}

          {/* Biometric Authentication Section - Always show */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className={`border rounded-lg p-6 ${biometricSupported ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${biometricSupported ? 'bg-gradient-to-br from-blue-500 to-indigo-600' : 'bg-slate-400'}`}>
                  <Fingerprint className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    Biometrische Anmeldung
                  </h3>
                  <p className="text-sm text-slate-600">
                    {biometricSupported 
                      ? (biometricStatus.biometric_enabled 
                          ? "Fingerabdruck/Face ID ist aktiviert" 
                          : "Mit Fingerabdruck oder Face ID anmelden")
                      : "Nicht verfügbar auf diesem Gerät"}
                  </p>
                </div>
              </div>
              
              {!biometricSupported ? (
                <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-600">
                  <p className="mb-2"><strong>Biometrie ist nicht verfügbar:</strong></p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>WebAuthn API: {biometricDebug.webauthn ? '✅ Unterstützt' : '❌ Nicht unterstützt'}</li>
                    <li>Fingerabdruck/Face ID: {biometricDebug.platform ? '✅ Verfügbar' : '❌ Nicht verfügbar'}</li>
                  </ul>
                  <p className="mt-3 text-xs text-slate-500">
                    Biometrische Anmeldung benötigt ein Gerät mit Fingerabdruck-Sensor oder Face ID 
                    und einen kompatiblen Browser (Chrome, Safari, Edge).
                  </p>
                </div>
              ) : biometricStatus.biometric_enabled ? (
                <div>
                  <div className="flex items-center gap-2 text-green-700 bg-green-100 px-4 py-3 rounded-lg mb-4">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Biometrische Anmeldung ist aktiv</span>
                  </div>
                  {biometricStatus.devices?.length > 0 && (
                    <p className="text-sm text-slate-600 mb-4">
                      Eingerichtet auf: {biometricStatus.devices.map(d => d.name).join(', ')}
                    </p>
                  )}
                  <Button 
                    onClick={handleDisableBiometric}
                    disabled={biometricLoading}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {biometricLoading ? "Wird deaktiviert..." : "Deaktivieren"}
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-slate-600 mb-4 text-sm">
                    Aktiviere die biometrische Anmeldung, um dich schnell und sicher 
                    mit deinem Fingerabdruck oder Face ID anzumelden.
                  </p>
                  <Button 
                    onClick={handleEnableBiometric}
                    disabled={biometricLoading}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                  >
                    <Fingerprint className="w-4 h-4 mr-2" />
                    {biometricLoading ? "Wird eingerichtet..." : "Jetzt aktivieren"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Notifications Section */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className={`border rounded-xl p-6 ${notificationStatus.supported ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${notificationStatus.enabled ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-slate-400'}`}>
                  {notificationStatus.enabled ? <Bell className="w-6 h-6 text-white" /> : <BellOff className="w-6 h-6 text-white" />}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    Benachrichtigungen
                  </h3>
                  <p className="text-sm text-slate-600">
                    {!notificationStatus.supported 
                      ? "Nicht verfügbar in diesem Browser"
                      : notificationStatus.enabled 
                        ? "Push-Benachrichtigungen sind aktiviert" 
                        : "Erhalte Erinnerungen und Motivation"}
                  </p>
                </div>
              </div>
              
              {/* Status Info - compact version */}
              <div className="mb-4 p-2 bg-white/50 rounded-lg text-xs">
                <p>📱 Push: {notificationStatus.supported && notificationStatus.permission === 'granted' ? '✅' : '❌'}</p>
              </div>
              
              {!notificationStatus.supported ? (
                <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-600">
                  <p>Dein Browser unterstützt keine Benachrichtigungen.</p>
                  <p className="mt-2 text-xs text-slate-500">
                    Verwende Chrome, Firefox, Edge oder Safari für Benachrichtigungen.
                  </p>
                </div>
              ) : notificationStatus.permission === 'denied' ? (
                <div className="bg-red-100 rounded-lg p-4 text-sm text-red-700">
                  <p className="font-medium">Benachrichtigungen wurden blockiert</p>
                  <p className="mt-2 text-xs">
                    Bitte erlaube Benachrichtigungen in den App-Einstellungen:
                  </p>
                  <Button 
                    onClick={openAppNotificationSettings}
                    className="mt-3 w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    <SettingsIcon className="w-4 h-4 mr-2" />
                    App-Einstellungen öffnen
                  </Button>
                </div>
              ) : notificationStatus.enabled ? (
                <div className="space-y-3">
                  {/* Notification Types - Clean Grid */}
                  <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
                    {/* Termin-Erinnerung */}
                    <div className="p-4 border-b border-amber-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">⏰</span>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">Termin-Erinnerung</p>
                            <p className="text-xs text-slate-500">Vor jedem Termin</p>
                          </div>
                        </div>
                        <input 
                          type="checkbox" 
                          checked={notificationSettings.appointment_reminder !== false}
                          onChange={(e) => handleNotificationSettingChange('appointment_reminder', e.target.checked)}
                          className="w-6 h-6 accent-amber-500 rounded"
                        />
                      </div>
                      <div className="mt-2 pl-9">
                        <select
                          value={notificationSettings.reminder_minutes || 15}
                          onChange={(e) => handleNotificationSettingChange('reminder_minutes', parseInt(e.target.value))}
                          disabled={!notificationSettings.appointment_reminder}
                          className="w-full text-sm p-2 border border-slate-200 rounded-lg bg-slate-50"
                        >
                          <option value={5}>5 Minuten vorher</option>
                          <option value={10}>10 Minuten vorher</option>
                          <option value={15}>15 Minuten vorher</option>
                          <option value={30}>30 Minuten vorher</option>
                          <option value={60}>1 Stunde vorher</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Tägliche Übersicht */}
                    <div className="p-4 border-b border-amber-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">📅</span>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">Tägliche Übersicht</p>
                            <p className="text-xs text-slate-500">Deine Termine für heute</p>
                          </div>
                        </div>
                        <input 
                          type="checkbox" 
                          checked={notificationSettings.daily_overview !== false}
                          onChange={(e) => handleNotificationSettingChange('daily_overview', e.target.checked)}
                          className="w-6 h-6 accent-amber-500 rounded"
                        />
                      </div>
                      <div className="mt-2 pl-9">
                        <input
                          type="time"
                          value={notificationSettings.daily_overview_time || "07:00"}
                          onChange={(e) => handleNotificationSettingChange('daily_overview_time', e.target.value)}
                          disabled={!notificationSettings.daily_overview}
                          className="w-full text-sm p-2 border border-slate-200 rounded-lg bg-slate-50"
                        />
                      </div>
                    </div>
                    
                    {/* Fokus-Erinnerung */}
                    <div className="p-4 border-b border-amber-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">🎯</span>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">Fokus-Erinnerung</p>
                            <p className="text-xs text-slate-500">Dein Tagesziel</p>
                          </div>
                        </div>
                        <input 
                          type="checkbox" 
                          checked={notificationSettings.focus_reminder !== false}
                          onChange={(e) => handleNotificationSettingChange('focus_reminder', e.target.checked)}
                          className="w-6 h-6 accent-amber-500 rounded"
                        />
                      </div>
                      <div className="mt-2 pl-9">
                        <input
                          type="time"
                          value={notificationSettings.focus_reminder_time || "07:00"}
                          onChange={(e) => handleNotificationSettingChange('focus_reminder_time', e.target.value)}
                          disabled={!notificationSettings.focus_reminder}
                          className="w-full text-sm p-2 border border-slate-200 rounded-lg bg-slate-50"
                        />
                      </div>
                    </div>
                    
                    {/* Motivations-Zitat */}
                    <div className="p-4 border-b border-amber-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">💫</span>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">Motivations-Zitat</p>
                            <p className="text-xs text-slate-500">Täglicher Impuls</p>
                          </div>
                        </div>
                        <input 
                          type="checkbox" 
                          checked={notificationSettings.motivational_quote !== false}
                          onChange={(e) => handleNotificationSettingChange('motivational_quote', e.target.checked)}
                          className="w-6 h-6 accent-amber-500 rounded"
                        />
                      </div>
                      <div className="mt-2 pl-9">
                        <input
                          type="time"
                          value={notificationSettings.motivational_quote_time || "08:00"}
                          onChange={(e) => handleNotificationSettingChange('motivational_quote_time', e.target.value)}
                          disabled={!notificationSettings.motivational_quote}
                          className="w-full text-sm p-2 border border-slate-200 rounded-lg bg-slate-50"
                        />
                      </div>
                    </div>
                    
                    {/* Wochenplanung */}
                    <div className="p-4 border-b border-amber-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">📋</span>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">Wochenplanung</p>
                            <p className="text-xs text-slate-500">Sonntag Abend</p>
                          </div>
                        </div>
                        <input 
                          type="checkbox" 
                          checked={notificationSettings.weekly_planning !== false}
                          onChange={(e) => handleNotificationSettingChange('weekly_planning', e.target.checked)}
                          className="w-6 h-6 accent-amber-500 rounded"
                        />
                      </div>
                      <div className="mt-2 pl-9">
                        <input
                          type="time"
                          value={notificationSettings.weekly_planning_time || "19:00"}
                          onChange={(e) => handleNotificationSettingChange('weekly_planning_time', e.target.value)}
                          disabled={!notificationSettings.weekly_planning}
                          className="w-full text-sm p-2 border border-slate-200 rounded-lg bg-slate-50"
                        />
                      </div>
                    </div>
                    
                    {/* Monatsplanung */}
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">🗓️</span>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">Monatsplanung</p>
                            <p className="text-xs text-slate-500">Am 1. des Monats</p>
                          </div>
                        </div>
                        <input 
                          type="checkbox" 
                          checked={notificationSettings.monthly_planning !== false}
                          onChange={(e) => handleNotificationSettingChange('monthly_planning', e.target.checked)}
                          className="w-6 h-6 accent-amber-500 rounded"
                        />
                      </div>
                      <div className="mt-2 pl-9">
                        <input
                          type="time"
                          value={notificationSettings.monthly_planning_time || "10:00"}
                          onChange={(e) => handleNotificationSettingChange('monthly_planning_time', e.target.value)}
                          disabled={!notificationSettings.monthly_planning}
                          className="w-full text-sm p-2 border border-slate-200 rounded-lg bg-slate-50"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Follow-up Erinnerungen — eigene Section */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="p-4 border-b border-slate-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">📞</span>
                          <div>
                            <p className="font-medium text-slate-800 text-sm">Follow-up Erinnerungen</p>
                            <p className="text-xs text-slate-500">Tägliche Push für fällige Kontakt-Follow-ups</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={notificationSettings.followup_reminder !== false}
                          onChange={(e) => handleNotificationSettingChange('followup_reminder', e.target.checked)}
                          className="w-6 h-6 accent-amber-500 rounded"
                          data-testid="followup-toggle-master"
                        />
                      </div>
                      <div className="mt-3 pl-9">
                        <label className="text-xs text-slate-500 mb-1 block">Uhrzeit</label>
                        <input
                          type="time"
                          value={notificationSettings.followup_reminder_time || "09:00"}
                          onChange={(e) => handleNotificationSettingChange('followup_reminder_time', e.target.value)}
                          disabled={notificationSettings.followup_reminder === false}
                          className="w-full text-sm p-2 border border-slate-200 rounded-lg bg-slate-50"
                          data-testid="followup-time-input"
                        />
                      </div>
                    </div>
                    {/* Welche Stufen */}
                    <div className="p-4 space-y-3">
                      <p className="text-xs text-slate-500 font-medium">Welche Stufen erinnern?</p>
                      {[
                        { key: 'followup_7_enabled',  label: '7-Tage Follow-up'  },
                        { key: 'followup_14_enabled', label: '14-Tage Follow-up' },
                        { key: 'followup_21_enabled', label: '21-Tage Follow-up' },
                        { key: 'followup_28_enabled', label: '28-Tage Follow-up' },
                      ].map(stage => (
                        <label key={stage.key} className="flex items-center justify-between cursor-pointer">
                          <span className="text-sm text-slate-700">{stage.label}</span>
                          <input
                            type="checkbox"
                            checked={notificationSettings[stage.key] !== false}
                            onChange={(e) => handleNotificationSettingChange(stage.key, e.target.checked)}
                            disabled={notificationSettings.followup_reminder === false}
                            className="w-5 h-5 accent-amber-500 rounded"
                            data-testid={`followup-stage-${stage.key}`}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Action Buttons - stacked on mobile */}
                  <div className="flex flex-col gap-2 pt-2">
                    <Button 
                      onClick={handleTestNotification}
                      variant="outline"
                      className="w-full"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Test senden
                    </Button>
                    <Button 
                      onClick={handleDisableNotifications}
                      variant="outline"
                      className="w-full border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <BellOff className="w-4 h-4 mr-2" />
                      Deaktivieren
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-600 mb-4">
                    Aktiviere Benachrichtigungen, um Erinnerungen, Motivation und Planungshilfen zu erhalten.
                  </p>
                  <Button 
                    onClick={handleEnableNotifications}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Benachrichtigungen aktivieren
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Kontakt-Erinnerungen Section */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl overflow-hidden">
              {/* Header – immer sichtbar, klickbar */}
              <button
                type="button"
                onClick={() => setShowContactSettings(v => !v)}
                className="w-full flex items-center gap-3 p-5 text-left hover:bg-emerald-100/60 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow flex-shrink-0">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-800">Kontakt-Erinnerungen</p>
                  <p className="text-xs text-slate-500">
                    {showContactSettings
                      ? "Schwellenwerte einstellen"
                      : `Gelb ab ${contactThresholds.yellowEnabled ? contactThresholds.yellowDays + ' T.' : '—'} · Rot ab ${contactThresholds.redEnabled ? contactThresholds.redDays + ' T.' : '—'}`}
                  </p>
                </div>
                {showContactSettings
                  ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
              </button>

              {/* Ausgeklappter Inhalt */}
              {showContactSettings && (
                <div className="px-5 pb-5 space-y-3 border-t border-emerald-200">
                  <p className="text-xs text-slate-500 pt-3">Ab wann gilt ein Kontakt als vernachlässigt?</p>

                  {/* Gelb */}
                  <div className="bg-white rounded-xl border border-yellow-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-3.5 h-3.5 rounded-full bg-yellow-400 flex-shrink-0" />
                        <span className="font-semibold text-slate-700 text-sm">Gelb – Längerer Kontaktverzug</span>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={contactThresholds.yellowEnabled}
                          onChange={e => handleContactThresholdChange('yellowEnabled', e.target.checked)}
                          className="w-5 h-5 accent-yellow-500 rounded"
                        />
                        <span className="text-xs text-slate-500">{contactThresholds.yellowEnabled ? 'Aktiv' : 'Keine Meldung'}</span>
                      </label>
                    </div>
                    <div className={`flex items-center gap-2 transition-opacity ${contactThresholds.yellowEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
                      <span className="text-sm text-slate-600">Ab</span>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={contactThresholds.yellowDays}
                        onChange={e => handleContactThresholdChange('yellowDays', Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-20 text-center text-sm font-semibold border border-yellow-300 rounded-lg px-2 py-1.5 bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      />
                      <span className="text-sm text-slate-600">Tagen ohne Kontakt</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Wird einmalig angezeigt – nach Verwerfen nicht mehr.</p>
                  </div>

                  {/* Rot */}
                  <div className="bg-white rounded-xl border border-red-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-3.5 h-3.5 rounded-full bg-red-500 flex-shrink-0" />
                        <span className="font-semibold text-slate-700 text-sm">Rot – Drohender Kontaktverlust</span>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={contactThresholds.redEnabled}
                          onChange={e => handleContactThresholdChange('redEnabled', e.target.checked)}
                          className="w-5 h-5 accent-red-500 rounded"
                        />
                        <span className="text-xs text-slate-500">{contactThresholds.redEnabled ? 'Aktiv' : 'Keine Meldung'}</span>
                      </label>
                    </div>
                    <div className={`flex items-center gap-2 transition-opacity ${contactThresholds.redEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
                      <span className="text-sm text-slate-600">Ab</span>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={contactThresholds.redDays}
                        onChange={e => handleContactThresholdChange('redDays', Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-20 text-center text-sm font-semibold border border-red-300 rounded-lg px-2 py-1.5 bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400"
                      />
                      <span className="text-sm text-slate-600">Tagen ohne Kontakt</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Wird wöchentlich neu angezeigt, bis Kontakt stattgefunden hat.</p>
                  </div>

                  <p className="text-xs text-slate-400">
                    Einstellungen werden sofort gespeichert und in allen Kontaktkarten übernommen.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* PayPal Donation Section */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Unterstützen Sie diese App
              </h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Diese Inventory Management App ist kostenlos. Falls sie Ihnen hilft, 
                können Sie die Entwicklung gerne mit einer kleinen Spende unterstützen.
              </p>
              <a 
                href="https://www.paypal.com/donate/?hosted_button_id=WPK5YE395ZHUJ"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-[#0070ba] hover:bg-[#005ea6] text-white rounded-lg transition-colors duration-200 font-medium shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a7.7 7.7 0 0 1-.32 1.433c-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9L7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81.298.34.563.729.774 1.107z"/>
                </svg>
                Spenden via PayPal
              </a>
              <p className="text-xs text-slate-400 mt-4">
                Vielen Dank für Ihre Unterstützung! 🙏
              </p>
            </div>
          </div>

          {/* Theme-Musik Player Toggle */}
          <div className="mt-8">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xl">🎵</span>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 text-sm">BurningTeam Theme-Song</p>
                    <p className="text-xs text-slate-500 truncate">
                      {musicDismissed ? 'Player ist ausgeblendet' : 'Player wird unten rechts angezeigt'}
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  data-testid="settings-music-toggle"
                  checked={!musicDismissed}
                  onChange={(e) => {
                    if (e.target.checked) {
                      localStorage.removeItem('bt_music_dismissed');
                      localStorage.setItem('bt_music_expanded', '1');
                      setMusicDismissed(false);
                      toast.success('Theme-Player wieder aktiviert');
                    } else {
                      localStorage.setItem('bt_music_dismissed', '1');
                      setMusicDismissed(true);
                      toast.info('Theme-Player ausgeblendet');
                    }
                    window.dispatchEvent(new Event('bt-music-visibility-change'));
                  }}
                  className="w-6 h-6 accent-orange-500 rounded flex-shrink-0"
                />
              </div>
            </div>
          </div>

          {/* Delete Account Section - Only show if logged in */}
          {isLoggedIn() && currentUser && (
            <div className="mt-12 pt-8 border-t border-red-200">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-red-800 mb-2 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Gefahrenzone
                </h3>
                <p className="text-red-700 mb-4 text-sm">
                  Das Löschen Ihres Accounts ist permanent und kann nicht rückgängig gemacht werden. 
                  Alle Ihre Daten (Rechnungen, Produkte, Bestände, etc.) werden unwiderruflich gelöscht.
                </p>
                <Button 
                  onClick={() => setShowDeleteModal(true)}
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Account löschen
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-red-100 p-3 rounded-full">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-slate-800 text-center mb-2">
                Account wirklich löschen?
              </h2>
              
              <p className="text-slate-600 text-center mb-6">
                Diese Aktion kann <strong>nicht rückgängig</strong> gemacht werden. 
                Alle Ihre Daten werden <strong>permanent gelöscht</strong>:
              </p>
              
              <ul className="text-sm text-slate-700 mb-6 space-y-2 bg-red-50 p-4 rounded-lg">
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>Alle Rechnungen und Kundendaten</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>Alle Produkte und Preislisten</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>Alle Bestandsdaten und Bewegungen</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>Alle Geschenke, Vorführartikel und Eigenverbrauch</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span>Ihr Benutzerkonto und alle Einstellungen</span>
                </li>
              </ul>

              <div className="flex gap-3">
                <Button 
                  onClick={() => setShowDeleteModal(false)}
                  variant="outline"
                  className="flex-1"
                  disabled={deleting}
                >
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleDeleteAccount}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  disabled={deleting}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleting ? "Wird gelöscht..." : "Endgültig löschen"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}